import { useTranslation } from 'react-i18next';

import { useToast } from '@/hooks/use-toast';
import {
  type ToastUpdate,
  bindDealItemsInProgressToastToDatasource,
  clearDealItemsInProgressToast,
  registerDealItemsInProgressToast,
} from '@/lib/deal-items-toast';
import {
  DatasourceInstructionMode,
  type DatasourceInstructionUpdateField,
  AddShopifyDataSourceToDealDocument,
} from '@/lib/graphql/generated/graphql';
import { useDealNotEditableHandler } from '@/lib/hooks/useDealNotEditableHandler';
import { getDomainErrorMessage } from '@/lib/utils/domain-error-translator';
import { useMutation } from '@apollo/client/react';

export function useShopifyFieldSync(dealId: string) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { handleIfDealNotEditable } = useDealNotEditableHandler();
  const [mutate, { loading }] = useMutation(AddShopifyDataSourceToDealDocument);

  const sync = async (params: {
    dealItemsIds: string[];
    fields: DatasourceInstructionUpdateField[];
    inProgressMessage?: string;
    onSuccess?: () => void;
  }) => {
    const { dealItemsIds, fields, inProgressMessage, onSuccess } = params;

    const countSuffix = t('general.sync_in_progress_with_count', {
      count: dealItemsIds.length,
    });
    const { update, dismiss } = toast({
      description: inProgressMessage
        ? `⏳ ${inProgressMessage} ${countSuffix}`
        : `⏳ ${countSuffix}`,
      duration: Number.POSITIVE_INFINITY,
    });
    const toastTrackerId = registerDealItemsInProgressToast(
      update as ToastUpdate,
      dismiss
    );

    try {
      const result = await mutate({
        variables: {
          dealId,
          dealItemsIds,
          instructionMode: DatasourceInstructionMode.UpdateOnly,
          fields,
        },
      });
      const mutationResult = result.data?.addShopifyDataSourceToDeal;

      if (mutationResult?.__typename === 'MutationSuccess') {
        bindDealItemsInProgressToastToDatasource(
          toastTrackerId,
          mutationResult.id
        );
        onSuccess?.();
      } else {
        clearDealItemsInProgressToast({ trackerId: toastTrackerId });

        if (
          mutationResult?.__typename === 'DomainException' &&
          handleIfDealNotEditable(mutationResult.code)
        ) {
          return;
        }

        toast({
          title: t('general.error_sync'),
          description:
            mutationResult?.__typename === 'DomainException'
              ? getDomainErrorMessage(
                  mutationResult.code,
                  t,
                  'general.error_unknown'
                )
              : t('general.error_unknown'),
          variant: 'destructive',
        });
      }
    } catch (e) {
      clearDealItemsInProgressToast({ trackerId: toastTrackerId });
      console.error('[useShopifyFieldSync] Error:', e);
      toast({
        title: t('general.error_sync'),
        description:
          e instanceof Error ? e.message : t('general.error_unknown'),
        variant: 'destructive',
      });
    }
  };

  return { sync, loading };
}
